// Renders the merged reverse-route tree (built by buildRouteTree) as a nested,
// indented tree rooted at the user. Each hop shows its host, worst per-hop loss
// and latency, and badges for the tiers whose path passes through it.

import type { RouteTreeNode } from '@/engine/route';
import { pct, ms } from '@/lib/format';
import { TierBadge } from './components';

function severity(lossPct: number): 'ok' | 'warn' | 'bad' {
  if (lossPct >= 20) return 'bad';
  if (lossPct >= 5) return 'warn';
  return 'ok';
}

function HopRow({ node, depth }: { node: RouteTreeNode; depth: number }) {
  const isRoot = node.key === '__root';
  return (
    <li className="routehop">
      <div
        className={`routehop__row routehop__row--${isRoot ? 'root' : severity(node.worstLossPct)}`}
        style={{ paddingLeft: `${depth * 18}px` }}
      >
        <span className="routehop__host">{node.label}</span>
        {!isRoot ? (
          <span className="routehop__stats">
            {node.worstLossPct > 0 ? (
              <span className="routehop__loss">{pct(node.worstLossPct, 0)} loss</span>
            ) : null}
            <span className="routehop__ms">{ms(node.avgMs)}</span>
          </span>
        ) : null}
        {node.tiers.length ? (
          <span className="routehop__tiers">
            {node.tiers.map((t) => (
              <TierBadge key={t} tier={t} />
            ))}
          </span>
        ) : null}
      </div>
      {node.children.length ? (
        <ul className="routetree__children">
          {node.children.map((c) => (
            <HopRow key={c.key} node={c} depth={depth + 1} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function RouteTree({ root }: { root: RouteTreeNode }) {
  return (
    <ul className="routetree">
      <HopRow node={root} depth={0} />
    </ul>
  );
}
